// pages/PLScenarioPlayground.jsx - Mobile Responsive Version
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { usePlayground } from '../../hooks/playground/usePlayground';
import { useScenarios } from '../../hooks/playground/scenario/useScenarios';
import { useLineItemVisibility } from '../../hooks/playground/useLineItemVisibility';
import { Header } from '../../components/playground/Header';
import PLScenarioTable from '../../components/playground/sections/Default/PLScenarioTable';
import ScenarioModal from '../../components/playground/sections/Scenario/ScenarioModal';
import SaveScenarioModal from '../../components/playground/sections/Scenario/SaveScenarioModal';
import UnsavedChangesDialog from '../../components/playground/sections/Scenario/UnsavedChangesDialog';
import { LoadingSpinner, ErrorMessage } from '../../components/common';
import { useToast } from '../../hooks/useToast';
import { ToastContainer } from '../../components/common/Toast';

import { 
  getDefaultDateRange, 
  getCurrentCycleMonth,
  setDocumentMeta, 
  refreshDashboardCache 
} from '../../utils/dashboardUtils';
import { 
  DEFAULT_SECTION_STATE, 
  CACHE_ENDPOINTS, 
  DOCUMENT_TITLES 
} from '../../utils/dashboardConstants';

const PLScenarioPlayground = () => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const resetCustomItemsRef = React.useRef(null);
  const loadCustomItemsRef = React.useRef(null);
  const [customItems, setCustomItems] = useState({});
  const [showScenarioModal, setShowScenarioModal] = useState(false);
  const { toasts, removeToast } = useToast();
  const isLoadingScenarioRef = React.useRef(false);
  const isProgrammaticUpdateRef = React.useRef(false); 
  const [isRefreshingScenarios, setIsRefreshingScenarios] = useState(false);

  // ============================================================================
  // 1. DATA HOOKS
  // ============================================================================
  const {
    lineItems,
    months,
    currentMonth,
    currentYear: systemCurrentYear,
    availableYears,
    loading,
    error,
    lastSynced,
    averagingMethod,
    setAveragingMethod,
    accountingMethod,
    setAccountingMethod,
    updateAdjustment,
    applyToAllFuture,
    clearAllAdjustments,
    setAllAdjustments,
    userAdjustments,
    totals,
    refetch,
  } = usePlayground(selectedYear);

  // ============================================================================
  // 2. SCENARIO MANAGEMENT
  // ============================================================================
  const {
    currentScenarioId,
    currentScenarioName,
    currentScenario,
    hasUnsavedChanges,
    savedScenarios,
    isBaseline,
    isLoadingScenario,
    isSavingScenario,
    showSaveModal,
    saveModalMode,
    showUnsavedDialog,
    markAsChanged,
    markAsSaved,
    loadActual,
    loadScenario,
    newScenario,
    handleSelectScenario,
    createScenario,
    updateScenario,
    deleteScenario,
    openSaveModal,
    closeSaveModal,
    handleSaveModalSubmit,
    handleSaveAsNew,
    handleRename,
    handleEdit,
    handleDiscardChanges,
    handleUnsavedDialogUpdate,
    handleUnsavedDialogDiscard,
    handleUnsavedDialogCancel,
    scenariosLoaded,
    loadScenariosList,
    refreshScenarios,
    defaultScenarioId,
    isLoadingDefaultScenario,
    setAsDefault,
    clearDefault,
    loadDefaultScenario,
  } = useScenarios(selectedYear, availableYears);

  const hasAutoLoadedRef = useRef(false);

  // ✅ Auto-load default scenario on mount
  useEffect(() => {
    if (
      scenariosLoaded && 
      defaultScenarioId && 
      !currentScenarioId && 
      !isLoadingDefaultScenario &&
      !hasAutoLoadedRef.current  // ✅ Only run once
    ) {
      console.log('🚀 Auto-loading default scenario on mount');
      hasAutoLoadedRef.current = true;  // ✅ Mark as loaded
      
      loadDefaultScenario((scenarioData) => {
        console.log('📦 Applying default scenario data');
        setAveragingMethod(scenarioData.averagingMethod);
        setAllAdjustments(scenarioData.adjustments || {});
        if (loadCustomItemsRef.current) {
          loadCustomItemsRef.current(scenarioData.customItems || {});
        }
      });
    }
  }, [scenariosLoaded, defaultScenarioId, currentScenarioId, isLoadingDefaultScenario]);

  // ============================================================================
  // 3. VISIBILITY MANAGEMENT
  // ============================================================================
  const {
    hiddenItems,
    isEditMode: isVisibilityEditMode,
    showHiddenItems,
    isSaving: isSavingVisibility,
    hiddenCount,
    hasUnsavedChanges: hasUnsavedVisibilityChanges,
    toggleItemVisibility,
    isItemHidden,
    startEditing: startVisibilityEditing,
    saveChanges: saveVisibilityChanges,
    cancelEditing: cancelVisibilityEditing,
    toggleShowHidden,
    restoreItem,
  } = useLineItemVisibility();

  // ============================================================================
  // 4. WRAPPER FUNCTIONS FOR LOADING SCENARIOS
  // ============================================================================
  
  const handleLoadScenario = useCallback((scenarioId) => {
    isLoadingScenarioRef.current = true;
    isProgrammaticUpdateRef.current = true;
    
    loadScenario(scenarioId, (scenarioData) => {
      console.log('📦 [Page] Scenario data received:', scenarioData);
      
      setAveragingMethod(scenarioData.averagingMethod);
      setAllAdjustments(scenarioData.adjustments || {});
      
      if (loadCustomItemsRef.current) {
        loadCustomItemsRef.current(scenarioData.customItems || {});
      }
      
      setTimeout(() => {
        isLoadingScenarioRef.current = false;
        isProgrammaticUpdateRef.current = false;
      }, 100);
    });
  }, [loadScenario, setAveragingMethod, setAllAdjustments]);

  const handleLoadActual = useCallback(() => {
    isLoadingScenarioRef.current = true;
    isProgrammaticUpdateRef.current = true;
    
    loadActual(clearAllAdjustments, resetCustomItemsRef.current);
    
    if (loadCustomItemsRef.current) {
      loadCustomItemsRef.current({});
    }
    setCustomItems({});
    
    setTimeout(() => {
      isLoadingScenarioRef.current = false;
      isProgrammaticUpdateRef.current = false;
    }, 100);
  }, [loadActual, clearAllAdjustments]);

  const handleRefreshScenarios = useCallback(async () => {
    console.log('🔄 Refreshing scenarios list...');
    
    isProgrammaticUpdateRef.current = true;
    setIsRefreshingScenarios(true);
    
    try {
      await loadScenariosList();
      console.log('✅ Scenarios refreshed');
    } catch (error) {
      console.error('❌ Failed to refresh scenarios:', error);
    } finally {
      setIsRefreshingScenarios(false);
      setTimeout(() => {
        isProgrammaticUpdateRef.current = false;
      }, 100);
    }
  }, [loadScenariosList]);

  // ============================================================================
  // 5. SCENARIO ACTION HANDLERS
  // ============================================================================

  const handleScenarioSelect = useCallback((scenarioId) => {
    console.log('🎯 [Page] Scenario selected:', scenarioId);
    console.log('📊 [Page] Current scenario ID:', currentScenarioId);
    console.log('💾 [Page] Has unsaved changes:', hasUnsavedChanges);
    
    const result = handleSelectScenario(scenarioId);
    console.log('📦 [Page] Result from handleSelectScenario:', result);
    
    if (result?.action === 'loadActual') {
      console.log('✅ [Page] Loading actual');
      handleLoadActual();
    } else if (result?.action === 'loadScenario') {
      console.log('✅ [Page] Loading scenario:', result.scenarioId);
      handleLoadScenario(result.scenarioId);
    } else {
      console.log('⚠️ [Page] No action returned (likely showing unsaved dialog)');
    }
  }, [handleSelectScenario, handleLoadActual, handleLoadScenario, currentScenarioId, hasUnsavedChanges]);

  const handleUpdateExistingScenario = useCallback(async () => {
    if (isBaseline) {
      openSaveModal('new');
    } else {
      await updateScenario({
        name: currentScenarioName,
        description: currentScenario?.scenario_description,
        averagingMethod,
        adjustments: userAdjustments,
        customItems: customItems,
      });
    }
  }, [isBaseline, currentScenarioName, currentScenario, averagingMethod, userAdjustments, customItems, openSaveModal, updateScenario]);

  const handleSaveModalSubmitWithData = useCallback(async ({ name, description }) => {
    await handleSaveModalSubmit({
      name,
      description,
      averagingMethod,
      adjustments: userAdjustments,
      customItems: customItems,
    });
  }, [handleSaveModalSubmit, averagingMethod, userAdjustments, customItems]);

  const handleDeleteScenario = useCallback(async () => {
    const result = await deleteScenario();
    if (result?.shouldReloadBaseline) {
      handleLoadActual();
    }
  }, [deleteScenario, handleLoadActual]);

  const handleDiscardChangesClick = useCallback(() => {
    const result = handleDiscardChanges();
    
    if (result?.action === 'loadActual') {
      handleLoadActual();
    } else if (result?.action === 'loadScenario') {
      handleLoadScenario(result.scenarioId);
    }
  }, [handleDiscardChanges, handleLoadActual, handleLoadScenario]);

  const handleUnsavedDialogUpdateClick = useCallback(async () => {
    const result = await handleUnsavedDialogUpdate({
      averagingMethod,
      adjustments: userAdjustments,
      customItems: customItems,
    });
    
    if (result?.action === 'loadActual') {
      handleLoadActual();
    } else if (result?.action === 'loadScenario') {
      handleLoadScenario(result.scenarioId);
    }
  }, [handleUnsavedDialogUpdate, averagingMethod, userAdjustments, customItems, handleLoadActual, handleLoadScenario]);

  const handleUnsavedDialogDiscardClick = useCallback(() => {
    const result = handleUnsavedDialogDiscard();
    
    if (result?.action === 'loadActual') {
      handleLoadActual();
    } else if (result?.action === 'loadScenario') {
      handleLoadScenario(result.scenarioId);
    }
  }, [handleUnsavedDialogDiscard, handleLoadActual, handleLoadScenario]);

  // ============================================================================
  // 6. UI EVENT HANDLERS
  // ============================================================================

  const handleCustomItemsChange = useCallback((items) => {
    // console.log('📝 [Page] Custom items changed:', Object.keys(items).length);
    setCustomItems(items);
    
    if (Object.keys(items).length > 0 && !isLoadingScenarioRef.current && !isProgrammaticUpdateRef.current) {
      // console.log('✅ Marking as changed (user edit)');
      markAsChanged();
    }
  }, [markAsChanged]);

  const handleYearChange = useCallback((year) => {
    // console.log('📅 [Page] Year changed to:', year);
    setSelectedYear(year);
  }, []);

  const handleAveragingMethodChange = useCallback((e) => {
    const newMethod = e.target.value;
    // console.log('📊 [Page] Averaging method changed to:', newMethod);
    setAveragingMethod(newMethod);
    
    if (!isLoadingScenarioRef.current && !isProgrammaticUpdateRef.current) {
      markAsChanged();
    }
  }, [setAveragingMethod, markAsChanged]);

  const handleAccountingMethodChange = useCallback((e) => {
    const newMethod = e.target.value;
    // console.log('💰 [Page] Accounting method changed to:', newMethod);
    
    if (Object.keys(userAdjustments).length > 0) {
      if (window.confirm(
        `Switching from ${accountingMethod} to ${newMethod} will clear all your manual adjustments. Continue?`
      )) {
        setAccountingMethod(newMethod);
      }
    } else {
      setAccountingMethod(newMethod);
    }
  }, [accountingMethod, setAccountingMethod, userAdjustments]);

  const handleClearAdjustments = useCallback(() => {
    if (window.confirm('Clear all adjustments and reset to calculated projections?')) {
      // console.log('🗑️ [Page] Clearing all adjustments');
      clearAllAdjustments();
      if (resetCustomItemsRef.current) {
        resetCustomItemsRef.current();
      }
      setCustomItems({});
    }
  }, [clearAllAdjustments]);

  const handleResetCallback = useCallback((resetFn) => {
    resetCustomItemsRef.current = resetFn;
  }, []);

  const handleLoadCustomItemsCallback = useCallback((loadFn) => {
    loadCustomItemsRef.current = loadFn;
  }, []);

  const handleUpdateAdjustment = useCallback((accountId, monthNum, value, year) => {
    updateAdjustment(accountId, monthNum, value, year);
    
    if (!isLoadingScenarioRef.current && !isProgrammaticUpdateRef.current) {
      markAsChanged();
    }
  }, [updateAdjustment, markAsChanged]);

  const handleNewScenario = useCallback(() => {
    newScenario(clearAllAdjustments, resetCustomItemsRef.current);
    
    if (loadCustomItemsRef.current) {
      loadCustomItemsRef.current({});
    }
    setCustomItems({});
    
    openSaveModal('new');
  }, [newScenario, clearAllAdjustments, openSaveModal]);

  // ============================================================================
  // DERIVED STATE
  // ============================================================================
  const hasAdjustments = hasUnsavedChanges; 

  // ============================================================================
  // EFFECTS
  // ============================================================================
  
  // Set document title and favicon - use shared utility
  useEffect(() => {
    setDocumentMeta(DOCUMENT_TITLES.PLAYGROUND);
  }, []);

  // ============================================================================
  // ============================================================================

  // ============================================================================
  // LOADING & ERROR STATES
  // ============================================================================
  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} onRetry={refetch} />;

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <div className="page-wrapper min-h-screen bg-gray-50">
      {/* Toast notifications - positioned for mobile */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      
      <div className="layout-container responsive-space">
        {/* ===== HEADER ===== */}
        <Header
          loading={loading}
          lastUpdated={lastSynced}
          selectedYear={selectedYear}
          onYearChange={handleYearChange}
          availableYears={availableYears}
          currentYear={systemCurrentYear}
          averagingMethod={averagingMethod}
          onAveragingMethodChange={handleAveragingMethodChange}
          accountingMethod={accountingMethod}
          onAccountingMethodChange={handleAccountingMethodChange}
          currentMonth={currentMonth}
          
          // Scenario props
          scenarios={savedScenarios}
          currentScenarioId={currentScenarioId}
          currentScenarioName={currentScenarioName}
          hasUnsavedScenarioChanges={hasUnsavedChanges}
          onSelectScenario={handleScenarioSelect}
          onSaveScenario={handleNewScenario}
          onUpdateScenario={handleUpdateExistingScenario}
          onSaveAsNew={handleSaveAsNew}
          onRenameScenario={handleRename}
          onDeleteScenario={handleDeleteScenario}
          onDiscardChanges={handleDiscardChangesClick}
          onOpenScenarioManager={() => setShowScenarioModal(true)}
          
          // Loading states
          isLoadingScenario={isLoadingScenario}
          isSavingScenario={isSavingScenario}
          onRefreshScenarios={handleRefreshScenarios}
          isRefreshingScenarios={isRefreshingScenarios}
        />

        {/* ===== P&L TABLE ===== */}
        {/* Mobile: Remove padding, add overflow scroll */}
        {/* Desktop: Normal spacing */}
        <div className="mt-4 sm:mt-6 bg-white rounded-lg shadow-sm overflow-hidden">
          {/* Mobile: Horizontal scroll container */}
          <div className="overflow-x-auto -mx-2 sm:mx-0">
            <PLScenarioTable
              lineItems={lineItems}
              months={months}
              currentMonth={currentMonth}
              selectedYear={selectedYear}
              onYearChange={handleYearChange}
              currentYear={systemCurrentYear}
              availableYears={availableYears}
              totals={totals}
              
              // Adjustment handling
              onUpdateAdjustment={handleUpdateAdjustment}
              onApplyToAllFuture={applyToAllFuture}
              userAdjustments={userAdjustments}
              onResetAdjustments={handleClearAdjustments}
              hasAdjustments={hasAdjustments}
              
              // Custom items handling
              onResetCallback={handleResetCallback}
              onLoadTemporaryRowsCallback={handleLoadCustomItemsCallback}
              onTemporaryRowsChange={handleCustomItemsChange}
              
              // Visibility handling
              hiddenItems={hiddenItems}
              isVisibilityEditMode={isVisibilityEditMode}
              showHiddenItems={showHiddenItems}
              isSavingVisibility={isSavingVisibility}
              hiddenCount={hiddenCount}
              hasUnsavedVisibilityChanges={hasUnsavedVisibilityChanges}
              onToggleItemVisibility={toggleItemVisibility}
              onIsItemHidden={isItemHidden}
              onStartVisibilityEditing={startVisibilityEditing}
              onSaveVisibilityChanges={saveVisibilityChanges}
              onCancelVisibilityEditing={cancelVisibilityEditing}
              onToggleShowHidden={toggleShowHidden}
              onRestoreItem={restoreItem}
            />
          </div>
        </div>

        {/* ===== MODALS ===== */}
        {/* Save Scenario Modal - Mobile optimized */}
        <SaveScenarioModal
          isOpen={showSaveModal}
          onClose={closeSaveModal}
          onSave={handleSaveModalSubmitWithData}      // ✅ Use the wrapper that already has the data
          mode={saveModalMode}
          initialName={saveModalMode === 'edit' || saveModalMode === 'rename' ? currentScenarioName : ''}
          initialDescription={saveModalMode === 'edit' ? currentScenario?.scenario_description : ''}
          isSaving={isSavingScenario}
        />

        {/* Unsaved Changes Dialog - Mobile optimized */}
        <UnsavedChangesDialog
          isOpen={showUnsavedDialog}
          scenarioName={currentScenarioName}
          onUpdate={handleUnsavedDialogUpdateClick}
          onDiscard={handleUnsavedDialogDiscardClick}
          onCancel={handleUnsavedDialogCancel}
        />

        {/* Scenario Manager Modal - Mobile optimized */}
        {/* Scenario Manager Modal - Mobile optimized */}
        {showScenarioModal && (
          <ScenarioModal
            isOpen={showScenarioModal}
            onClose={() => setShowScenarioModal(false)}
            scenarios={savedScenarios}
            scenariosLoaded={scenariosLoaded}
            onLoadScenarios={loadScenariosList}
            currentScenarioId={currentScenarioId}
            currentScenarioName={currentScenarioName}
            hasUnsavedChanges={hasUnsavedChanges}
            selectedYear={selectedYear}
            currentYear={currentYear}
            currentMonth={currentMonth}
            averagingMethod={averagingMethod}
            adjustments={userAdjustments}              // ✅ Fixed: userAdjustments
            customItems={customItems}                  // ✅ Fixed: customItems
            lineItems={lineItems}
            defaultScenarioId={defaultScenarioId}
            onSetAsDefault={setAsDefault}
            
            // ✅ Connect actions with correct variable names
            onLoadScenario={(scenarioId) => {
              handleLoadScenario(scenarioId);          // ✅ Fixed: use existing wrapper
            }}
            onDeleteScenario={handleDeleteScenario}    // ✅ Fixed: use existing wrapper
            onNewScenario={() => {
              handleNewScenario();                     // ✅ Fixed: use existing wrapper
              setShowScenarioModal(false);
            }}
            onLoadActual={() => {
              handleLoadActual();                      // ✅ Fixed: use existing wrapper
              setShowScenarioModal(false);
            }}
            // onEdit={() => openSaveModal('edit')}
            onSaveAsNew={() => openSaveModal('saveAs')}
            onUpdateScenario={updateScenario}
            
            isLoadingScenario={isLoadingScenario}
            isSavingScenario={isSavingScenario}
          />
        )}
      </div>
    </div>
  );
};

export default PLScenarioPlayground;